import { Link, useLocation } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import { Logo } from "@/components/ui-custom/Logo";
import { SITE, RESIDENTIAL_NAV, COMMERCIAL_NAV } from "@/data/site";

const ColHeading = ({ children }) => (
  <div className="eyebrow eyebrow-start" style={{ marginBottom: "20px" }}>
    {children}
  </div>
);

const FLink = ({ to, children, testId }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  const baseColor = active ? "var(--gold)" : "rgba(245,242,236,0.68)";
  return (
    <Link
      to={to}
      data-testid={testId}
      aria-current={active ? "page" : undefined}
      className="block py-1.5"
      style={{ color: baseColor, fontSize: "15px", fontWeight: active ? 600 : 400, transition: "color 0.18s ease" }}
      onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
      onMouseLeave={(e) => (e.currentTarget.style.color = baseColor)}
    >
      {children}
    </Link>
  );
};

export const Footer = () => {
  return (
    <footer style={{ background: "var(--black)", borderTop: "1px solid rgba(245,207,112,0.35)" }} data-testid="site-footer">
      <div className="container-1200 section-y">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <Logo height={72} />
            <p className="mt-6" style={{ color: "rgba(245,242,236,0.6)", fontSize: "15px", lineHeight: 1.6, maxWidth: "280px" }}>
              {SITE.tagline}
            </p>
            <div className="flex gap-3 mt-6">
              {[Facebook, Instagram, Linkedin].map((Ico, i) => (
                <span key={i} className="flex items-center justify-center" style={{ width: 40, height: 40, borderRadius: 8, border: "1px solid var(--border-dark)", color: "var(--gold)" }}>
                  <Ico size={18} strokeWidth={1.5} />
                </span>
              ))}
            </div>
          </div>

          <div>
            <ColHeading>Residential</ColHeading>
            {RESIDENTIAL_NAV.map((it) => (
              <FLink key={it.path} to={it.path} testId={`footer-${it.path.split("/").pop()}`}>
                {it.label}
              </FLink>
            ))}
          </div>

          <div>
            <ColHeading>Commercial</ColHeading>
            {COMMERCIAL_NAV.map((it) => (
              <FLink key={it.path} to={it.path} testId={`footer-${it.path.split("/").pop()}`}>
                {it.label}
              </FLink>
            ))}
            <div className="mt-3">
              <FLink to="/service-areas">Service Areas</FLink>
              <FLink to="/contact">Contact</FLink>
              <FLink to="/legal">Legal</FLink>
            </div>
          </div>

          <div>
            <ColHeading>Get in touch</ColHeading>
            <div className="flex items-start gap-3 mb-4">
              <MapPin size={18} strokeWidth={1.5} style={{ color: "var(--gold)", marginTop: 3 }} />
              <span style={{ color: "rgba(245,242,236,0.68)", fontSize: "15px", lineHeight: 1.55 }}>
                {SITE.address.full}
              </span>
            </div>
            <a href={SITE.phoneHref} data-testid="footer-phone" className="flex items-center gap-3 mb-4" style={{ color: "rgba(245,242,236,0.68)", fontSize: "15px" }}>
              <Phone size={18} strokeWidth={1.5} style={{ color: "var(--cta)" }} />
              {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="flex items-center gap-3" style={{ color: "rgba(245,242,236,0.68)", fontSize: "15px" }}>
              <Mail size={18} strokeWidth={1.5} style={{ color: "var(--gold)" }} />
              {SITE.email}
            </a>
          </div>
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--border-dark)" }}>
        <div className="container-1200 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-center md:text-left">
          <span style={{ color: "rgba(245,242,236,0.5)", fontSize: "13px" }}>
            © 2026 {SITE.name} · NJ HIC {SITE.hicNumber} · Licensed &amp; Insured
          </span>
          <span data-testid="footer-signature" style={{ color: "rgba(245,242,236,0.42)", fontSize: "13px" }}>
            Designed &amp; Developed by{" "}
            <a href="https://pharosgrowth.com" target="_blank" rel="noopener noreferrer" data-testid="footer-pharos-link" className="hover:text-gold" style={{ color: "rgba(245,242,236,0.6)", textDecoration: "underline", textUnderlineOffset: "2px" }}>
              Pharos Growth
            </a>
          </span>
          <span style={{ color: "rgba(245,242,236,0.5)", fontSize: "13px" }}>
            <Link to="/legal" className="hover:text-gold">Privacy Policy</Link> · <Link to="/legal" className="hover:text-gold">Terms</Link>
          </span>
        </div>
      </div>
    </footer>
  );
};
